import Big from 'big.js';

import {
  ApiProduct, ApiProductStockDetail, ApiProductColor
} from 'idt-ecommerce-api/_api/api-catalog/api-products/api-products.models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { UrlsWebHelpers } from 'idt-ecommerce-api/util/urls-web.helpers';
// eslint-disable-next-line no-restricted-imports
import { GrowthbookConfig } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';

import { MoneyHelpers } from '../../../util/money.helpers';
import { MediasModelsTransformer } from '../medias/medias.models.transformer';
import { ProductLabel } from '../metadata/product-metadata.models';
import {
  Product,
  ProductColor,
  ProductGridElementType,
  ProductTechnicalDetailModel,
  ProductPrice,
  ProductCompositionModel,
  ProductCareModel
} from '../products.models';
import { SizesModelsTransformer } from '../sizes/sizes.models.transformer';


export class ProductFeaturesModelsTransformer {

  public static setBundleInfoToProduct(apiProduct: ApiProduct, gridElemType: ProductGridElementType) {
    let rootProduct = apiProduct;
    if (isDefined(apiProduct.bundleProductSummaries) && apiProduct.bundleProductSummaries.length === 1) {
      rootProduct = apiProduct.bundleProductSummaries[0]; // main product
      rootProduct.mocacoOrEstilismoSourceProductId = rootProduct.id;
      rootProduct.id = apiProduct.id;
      rootProduct.gridElemType = gridElemType;
      rootProduct.mainColorid = apiProduct.mainColorid;
      rootProduct.detail.defaultImageType = apiProduct.detail.defaultImageType;
      rootProduct.detail.xmediaDefaultSet = apiProduct.detail.xmediaDefaultSet;
      rootProduct.detail.relatedProducts = apiProduct.detail.relatedProducts;
      rootProduct.tags = apiProduct.tags;
      rootProduct.attributes = apiProduct.attributes;
      rootProduct.productUrlParam = apiProduct.productUrlParam;
      rootProduct.detail.fullReferenceBundle = apiProduct.detail.reference;
    }

    return rootProduct;
  }

  public static productColorObjectFromApi(
    apiProductColor: ApiProductColor,
    apiProduct: ApiProduct,
    context: EcommerceContext,
    stocks: ApiProductStockDetail[] = [],
    mainStyle: string | undefined = undefined,
    growthbookConfig: GrowthbookConfig[] = []
  ): ProductColor {
    const productColor: ProductColor = {
      id: apiProductColor.id,
      name: apiProductColor.name,
      sizes: SizesModelsTransformer.productSizeObjectFromApi(
        apiProductColor?.sizes ?? [], stocks, context, apiProduct?.productType, growthbookConfig
      ),
      medias: MediasModelsTransformer.mediasFromProductApi(apiProductColor, apiProduct, context),
      modelinfo: {
        height: apiProductColor.modelHeigh ? apiProductColor.modelHeigh : null,
        name: apiProductColor.modelName ? apiProductColor.modelName : null,
        size: apiProductColor.modelSize ? apiProductColor.modelSize : null
      },
      styleIds: [...new Set((apiProductColor.image?.style ?? []).concat(mainStyle ?? []))]
    };
    return productColor;
  }

  public static labelsFromMultipleProducts(bundleProducts: Product[]): ProductLabel[] {
    let labels: ProductLabel[] = [];
    bundleProducts.forEach((product) => {
      labels = labels.concat(product.metadata.labels);
    });
    return labels;
  }

  public static technicalDetails(apiProduct: ApiProduct, context: EcommerceContext) {
    const technicalDetails: ProductTechnicalDetailModel[] = [];
    if (apiProduct && apiProduct.attributes) {
      apiProduct.attributes.forEach((apiTechnicalDetail) => {
        if (apiTechnicalDetail.type === 'XESTFILTER') {
          technicalDetails.push({
            id: apiTechnicalDetail.id ? apiTechnicalDetail.id : '',
            image: UrlsWebHelpers.buildStaticUrl(context.store, `/itxwebstandard/images/technical/${apiTechnicalDetail.name}.png`),
            name: apiTechnicalDetail.name ? apiTechnicalDetail.name : '',
            title: apiTechnicalDetail.value ? apiTechnicalDetail.value : '',
            type: apiTechnicalDetail.type,
            description: apiTechnicalDetail.description ? apiTechnicalDetail.description : '',
            shortDescription: apiTechnicalDetail.shortDescription ? apiTechnicalDetail.shortDescription : ''
          });
        }
      });
    }
    return technicalDetails;
  }

  public static productComposition(apiProduct: ApiProduct) {
    const productComposition: ProductCompositionModel[] = [];
    if (apiProduct && apiProduct.detail && apiProduct.detail.composition) {
      apiProduct.detail.composition.forEach((apiProductComposition) => {
        if (apiProductComposition && apiProductComposition.part && apiProductComposition.composition) {
          apiProductComposition.composition.forEach((apiProductCompositionDetail) => {
            productComposition.push({
              name: apiProductCompositionDetail.name ? apiProductCompositionDetail.name : '',
              percentage: apiProductCompositionDetail.percentage ? apiProductCompositionDetail.percentage : '',
              part: apiProductComposition.part ? Number.parseInt(apiProductComposition.part, 10) : 0
            });
          });
        }
      });
    }
    return productComposition;
  }

  public static productCare(apiProduct: ApiProduct, context: EcommerceContext) {
    let productCare: ProductCareModel[] = [];
    if (apiProduct.detail.care) {
      productCare = apiProduct.detail.care.map((apiProductCare: ProductCareModel) => ({
        description: apiProductCare.description,
        image: UrlsWebHelpers.buildStaticUrl(context.store, `/images/cares/ginetex/${apiProductCare.name}.png`)
      }));
    }
    return productCare;
  }

  public static pricesFromMultipleProducts(bundleProducts: Product[], context: EcommerceContext): ProductPrice {
    const currencyInfo = context.store.details.currencyInfo;

    if (!currencyInfo) {
      return {};
    }

    const acumPrice: ProductPrice = bundleProducts.reduce((acc, product: Product) => {
      if (product?.priceInfo?.price) {
        acc.price += product.priceInfo.price;
      }
      if (product?.priceInfo?.oldPrice) {
        acc.oldPrice += product.priceInfo.oldPrice;
      }
      return acc;
    }, { price: 0, oldPrice: 0 });

    if (acumPrice.price) {
      acumPrice.priceWithCurrencySymbol = MoneyHelpers.formatPriceWithCurrencySymbol(acumPrice.price, currencyInfo);
    }
    if (acumPrice.oldPrice) {
      acumPrice.oldPriceWithCurrencySymbol = MoneyHelpers.formatPriceWithCurrencySymbol(
        acumPrice.oldPrice, currencyInfo
      );
    }

    return acumPrice;
  }

  public static alternateLocalePriceApplyingConversion(conversionRate: number, localePrice: number): number {
    const rate = new Big(conversionRate);
    const alternateLocalePrice = new Big(localePrice).mul(rate);
    return Number(alternateLocalePrice.toFixed(0));
  }

}
